import React, { useCallback, useContext, useMemo } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { useQuery } from "@apollo/client";
import { GET_MISSION_VACATION_STATUS, GET_PAYROLL_BREAKDOWN } from "../../graphql/timesheet";
import PayrollBreakdownHourly from "../../components/editors/payroll-breakdown/PayrollBreakdownHourly";
import PayrollBreakdownMonthly from "../../components/editors/payroll-breakdown/PayrollBreakdownMonthly";
import ScreenHeader from "./ScreenHeader";
import { useI18n } from "../../hooks/i18n-hooks";
import { usePayrollBreakdownFormatter } from "../../hooks/payroll-beakdown-utils";
import ScreenCard from "../ScreenCard";
import Loading from "../../components/Loading";
import Gap from "../../ui_atoms/Gap";
import { RootStackParamList } from "../../navigations/RootNavigation";
import VStack from "../../ui_atoms/VStack";
import { BodyMediumText } from "../../components/FormattedText";
import {
    PayrollBreakdownStatus,
    PayrollSourceType,
    ProfileType,
    Repetition,
    TimeSheetState,
} from "../../__generated__/graphql";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import MissionInfoItem from "../../components/MissionInfoItem";
import { FULL_DATE_FORMAT, useDateFormatter } from "../../hooks/date";
import PayrollBreakdownSummary from "../client/PayrollBreakdownSummary";
import { buildErrorHeadband } from "../ScreenCardHeadband";

const VIEWERS = {
    hourly: PayrollBreakdownHourly,
    daily: PayrollBreakdownHourly,
    monthly: PayrollBreakdownMonthly,
    package: PayrollBreakdownHourly,
    undefined: null,
};

type PayrollBreakdownDetailsScreenProps = StackScreenProps<
    RootStackParamList,
    "PayrollBreakdownDetails"
>;

const PayrollBreakdownDetailsScreen = ({
    route,
    navigation,
}: PayrollBreakdownDetailsScreenProps) => {
    const payrollBreakdownId = route.params.payrollBreakdownId;
    const i18n = useI18n();

    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const { data, error, loading, refetch } = useQuery(GET_PAYROLL_BREAKDOWN, {
        variables: { payrollBreakdownId },
        skip: !payrollBreakdownId,
    });
    const payrollBreakdown = data?.getPayrollBreakdown;

    const {
        data: vacationStatusData,
        error: vacationStatusError,
        loading: vacationStatusLoading,
        refetch: _vacationStatusRefetch,
    } = useQuery(GET_MISSION_VACATION_STATUS, {
        variables: { missionId: payrollBreakdown?.missionInfo.id || "" },
        skip: !payrollBreakdown,
        fetchPolicy: "cache-and-network",
    });
    const vacationStatusRefetch = payrollBreakdown ? _vacationStatusRefetch : () => {};
    const vacationStatus = vacationStatusData?.getMissionVacationStatus;

    const { formatPayrollDuration } = usePayrollBreakdownFormatter();
    const formatDate = useDateFormatter();
    const onBack = useCallback(() => {
        navigation && navigation.goBack();
    }, [navigation]);
    const Viewer = VIEWERS[payrollBreakdown?.missionInfo?.earningType || "undefined"];

    const allCompleted = payrollBreakdown?.timesheets?.every(
        (ts) => ts.status === TimeSheetState.Submitted || ts.status === TimeSheetState.Accepted,
    );
    const hasOpenTimesheet = payrollBreakdown?.timesheets?.some(
        (ts) => ts.status === TimeSheetState.Open,
    );
    const lastRejectedTimeSheet = payrollBreakdown?.timesheets?.find(
        (ts) => ts.status === TimeSheetState.Rejected,
    );
    const descriptionStatus = useMemo(() => {
        if (payrollBreakdown?.status === PayrollBreakdownStatus.Submitted) {
            if (payrollBreakdown?.submittedBy) {
                return "submitted";
            }
            return "auto_submitted";
        } else {
            if (payrollBreakdown?.missionInfo.payrollSource === PayrollSourceType.ManualClient) {
                return "external_ts";
            }
            if (allCompleted) {
                return "lift_ts_completed";
            }
            if (hasOpenTimesheet || lastRejectedTimeSheet) {
                return "lift_ts_open";
            }
        }
    }, [payrollBreakdown, allCompleted, hasOpenTimesheet, lastRejectedTimeSheet]);
    const subLabel = useMemo(() => {
        if (!payrollBreakdown) {
            return i18n.t("loading");
        }
        return formatPayrollDuration(payrollBreakdown);
    }, [payrollBreakdown, formatPayrollDuration, i18n]);

    const headerLabel = useMemo(() => {
        const repetition = payrollBreakdown?.missionInfo?.workingTimeClassicSpec?.repetition;
        switch (repetition) {
            case Repetition.None:
                return i18n.t("payroll_breakdown.nav_title_no_repetition");
            case Repetition.Week:
            case Repetition.Week2:
            case Repetition.Week3:
            case Repetition.Week4:
            case Repetition.Month:
            case Repetition.Day:
                return i18n.t("payroll_breakdown.nav_title");
            default:
                return " "; // loading...
        }
    }, [i18n, payrollBreakdown?.missionInfo?.workingTimeClassicSpec?.repetition]);
    return (
        <>
            <ScreenHeader
                label={headerLabel}
                subLabel={subLabel}
                onLeftAction={onBack}
                hideProfile
                useSmallLabel
            />
            <ScreenCard
                refresh={() => Promise.all([refetch, vacationStatusRefetch])}
                headband={
                    error || vacationStatusError
                        ? buildErrorHeadband(i18n.t("screens.payroll_breakdown.cannot_load"))
                        : undefined
                }
            >
                <Gap gap={1} />
                {!payrollBreakdown && (loading || vacationStatusLoading) && <Loading />}

                {profileType && payrollBreakdown && vacationStatus && Viewer && (
                    <VStack>
                        <VStack h="2/3">
                            <BodyMediumText>
                                {i18n.t(
                                    `screens.payroll_breakdown.info_text.${profileType}.${descriptionStatus}`,
                                    {
                                        workerName: payrollBreakdown.missionInfo?.workerInfo?.name,
                                        clientName: payrollBreakdown.submittedBy
                                            ? `${payrollBreakdown.submittedBy.firstName} ${payrollBreakdown.submittedBy.lastName}`
                                            : payrollBreakdown.missionInfo?.clientInfo?.profileName,
                                        companyName: payrollBreakdown.missionInfo?.clientInfo?.name,
                                        date: payrollBreakdown.submittedAt
                                            ? formatDate(
                                                  new Date(payrollBreakdown.submittedAt),
                                                  FULL_DATE_FORMAT,
                                              )
                                            : "",
                                    },
                                )}
                            </BodyMediumText>
                        </VStack>
                        <Gap gap={1} />
                        <MissionInfoItem missionInfo={payrollBreakdown.missionInfo} />
                        <Viewer
                            payrollBreakdown={payrollBreakdown}
                            vacationStatus={vacationStatus}
                            profileType={profileType}
                            navigation={navigation}
                        />
                        {profileType === ProfileType.Client && (
                            <>
                                <Gap gap={1} />
                                <PayrollBreakdownSummary
                                    rows={payrollBreakdown.summary.rows || []}
                                    needCompute={payrollBreakdown.summary.needProcessing}
                                    loading={loading}
                                    error={error}
                                    contractEarningType={payrollBreakdown?.missionInfo.earningType}
                                />
                            </>
                        )}
                    </VStack>
                )}
            </ScreenCard>
        </>
    );
};

export default PayrollBreakdownDetailsScreen;
